// ... (imports remain mostly same, remove firebase direct writes if not needed)
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowRight, Check, Copy, Twitter, Linkedin, Mail } from "lucide-react";
import { readAttribution } from "../../lib/attribution";

interface WaitlistModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  prompt?: string;
}

const WaitlistModal: React.FC<WaitlistModalProps> = ({ open, setOpen, prompt }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [locationData, setLocationData] = useState<any>(null);
  const [honeypot, setHoneypot] = useState("");

  // New State for Referral
  const [referralCode, setReferralCode] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    // 1. Capture Referral Code from URL
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) setReferredBy(refCode);

    // 2. Fetch Location
    fetch('https://get.geojs.io/v1/ip/geo.json')
      .then(res => res.json())
      .then(data => {
        if (data.ip) {
          setLocationData(data);
        }
      })
      .catch(err => console.error("Location fetch failed", err));
  }, []);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (honeypot) return; // Bot check

    if (!isValidEmail(email)) {
      alert("Please enter a valid email.");
      return;
    }

    setLoading(true);

    try {
      const ua = navigator.userAgent;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      // CALL NEW API API ROUTE
      const response = await fetch('/api/waitlist/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          prompt: (prompt || "").trim().slice(0, 500),
          referredBy: referredBy, // Send if exists
          location: locationData,
          device: isMobile ? "Mobile" : "Desktop",
          userAgent: ua,
          screenVal: `${window.screen.width}x${window.screen.height}`,
          attribution: readAttribution()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setReferralCode(data.referralCode);
        setSuccess(true);
        setStatusMessage(data.message || "");
        console.log("Joined waitlist with code:", data.referralCode);
      } else {
        throw new Error(data.message || "Failed to join");
      }

    } catch (error) {
      console.error("Error joining waitlist:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setSuccess(false);
      setReferralCode(""); // Reset
      setEmail("");
      setIsCopied(false);
      setStatusMessage("");
    }, 500);
  };

  const copyToClipboard = () => {
    const link = `${window.location.origin}/?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-white border border-zinc-200 p-0 mb-2 rounded-none shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden animate-in fade-in zoom-in-95 duration-300">

        {/* Modal Header: Terminal Bar */}
        <div className="bg-zinc-50 border-b border-zinc-200 px-4 py-2 flex items-center justify-between">
          <span className={`font-mono text-[10px] uppercase tracking-widest text-zinc-400`}>
            {success ? "ACCESS_WAITLISTED" : "ACCESS_RESTRICTED"}
          </span>
        </div>

        <div className="p-8">
          {success ? (
            <div className="flex flex-col items-center justify-center py-4 animate-in fade-in zoom-in duration-300">

              <div className="flex items-start justify-center gap-3 mb-8">
                {/* Techy Checkmark Box - Aligned with first line */}
                <div className="relative w-8 h-8 shrink-0 mt-[-2px]">
                  <div className="w-full h-full border-2 border-dashed border-[#0044FF] flex items-center justify-center rotate-45">
                    <Check className="text-[#0044FF] -rotate-45 w-5 h-5 animate-in zoom-in duration-500 delay-150" strokeWidth={4} />
                  </div>
                  {/* Decorative corners */}
                  <div className="absolute -top-1 -left-1 w-1.5 h-1.5 border-t-2 border-l-2 border-zinc-900"></div>
                  <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 border-b-2 border-r-2 border-zinc-900"></div>
                </div>

                <h3 className="font-mono text-xl md:text-2xl text-zinc-900 text-left tracking-widest leading-tight">
                  {statusMessage === 'Already on the list.' ? (
                    <>
                      <span className="block">You are already in</span>
                      <span className="block text-zinc-500 text-lg">Refer friends to move up</span>
                    </>
                  ) : (
                    <>
                      <span className="block">Refer friends</span>
                      <span className="block text-zinc-500 text-lg">to move up the queue</span>
                    </>
                  )}
                </h3>
              </div>

              {/* REFERRAL LINK BOX */}
              <div className="w-full bg-zinc-50 border border-zinc-200 p-4 mb-6 relative group">
                <p className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest mb-2"> Share the link </p>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm font-mono text-[#0044FF] truncate">
                    {typeof window !== 'undefined' ? window.location.origin : ''}/?ref={referralCode}
                  </code>
                  <button onClick={copyToClipboard} className="p-2 hover:bg-zinc-200 rounded-sm transition-colors" title="Copy Link">
                    {isCopied ? <Check size={16} className="text-green-600 animate-in zoom-in duration-300" /> : <Copy size={16} className="text-zinc-600" />}
                  </button>
                </div>
              </div>

              {/* SOCIAL SHARE BUTTONS */}
              <div className="flex gap-4">
                <a href={`https://twitter.com/intent/tweet?text=Just%20joined%20the%20waitlist%20for%20Monet.%20Check%20it%20out:&url=${typeof window !== 'undefined' ? window.location.origin : ''}/?ref=${referralCode}`} target="_blank" rel="noopener noreferrer" className="p-3 border border-zinc-200 text-zinc-600 hover:border-black hover:bg-black hover:text-white transition-all rounded-sm flex items-center justify-center w-12 h-12">
                  {/* Visual X using text or SVG */}
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${typeof window !== 'undefined' ? window.location.origin : ''}/?ref=${referralCode}`} target="_blank" rel="noopener noreferrer" className="p-3 border border-zinc-200 text-zinc-600 hover:bg-[#0077b5] hover:border-[#0077b5] hover:text-white transition-all rounded-sm flex items-center justify-center w-12 h-12">
                  <Linkedin size={18} strokeWidth={1.5} />
                </a>
                <a href={`mailto:?subject=Join%20me%20on%20Monet&body=Check%20out%20Monet:%20${typeof window !== 'undefined' ? window.location.origin : ''}/?ref=${referralCode}`} className="p-3 border border-zinc-200 text-zinc-600 hover:bg-red-500 hover:border-red-500 hover:text-white transition-all rounded-sm flex items-center justify-center w-12 h-12">
                  <Mail size={18} strokeWidth={1.5} />
                </a>
              </div>

            </div>
          ) : (
            <>
              <DialogHeader className="mb-8 space-y-2 text-left">
                <DialogTitle className="font-sans text-3xl text-zinc-900 uppercase tracking-tighter leading-none">
                  Join the <span className="text-[#FF3333]">Waitlist</span>
                </DialogTitle>
                <DialogDescription className="font-mono text-[12px] text-zinc-500 uppercase tracking-wide leading-relaxed">
                  System capacity is currently limited. <br />
                  Secure your position in the queue.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleEmailSubmit} className="flex flex-col gap-6">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-[10px] font-mono uppercase text-zinc-400 font-bold tracking-widest">
                    Email
                  </label>
                  <div className="relative group">
                    <Input
                      id="email"
                      type="email"
                      placeholder="user@domain.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-zinc-50 border-zinc-200 text-zinc-900 font-mono h-12 rounded-none border-b-2 focus:border-orange-600 focus:ring-0 transition-colors placeholder:text-zinc-400 pl-4"
                      autoFocus
                      required
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-focus-within:opacity-100 transition-opacity">
                      <div className="w-1.5 h-4 bg-orange-600 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* HONEYPOT FIELD (Hidden) */}
                <input
                  type="text"
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="absolute opacity-0 -z-10 w-0 h-0 pointer-events-none"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#111] hover:bg-black text-white font-mono font-bold text-xs uppercase py-4 tracking-widest transition-all hover:tracking-[0.2em] flex items-center justify-center gap-2 rounded-sm disabled:opacity-50 disabled:cursor-not-allowed border border-transparent shadow-lg shadow-black/10"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="animate-spin w-3 h-3" /> PROCESSING...
                    </span>
                  ) : (
                    <>
                      Join <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

        {/* Modal Footer: Decorative Details */}
        <div className="bg-zinc-50 border-t border-zinc-200 px-4 py-3 flex justify-between items-center">
          <span className="font-mono text-[9px] text-zinc-400 tracking-wider">STAY CREATIVE</span>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default WaitlistModal;
